import { apiClient } from '@/services/core/apiClient';

export interface VoicemailRecord {
  logId: number;
  patientName: string;
  patientNumber: string;
  patientId?: number;
  birthDate?: string;
  emr?: string;
  gender?: string;
  hccName: string;
  clinicNumber: string;
  startTime: string;
  duration?: string;
  recordingUrl?: string;
  callStatus?: string;
  callType?: string;
  hccId?: number;
  timezone?: string;
  careCoachName?: string;
  careCoachId?: number;
  podName?: string;
  podId?: number;
  sentiment?: string;
  summary?: string;
  answeredBy?: string;
  s3Key?: string;
}

export interface VoicemailResponse {
  callHistory: VoicemailRecord[];
  totalItems: number;
  totalPages: number;
  pageSize: number;
  page: number;
}

export interface VoicemailFilters {
  patientName?: string;
  clinicName?: string;
  clinicNumber?: string;
  patientNumber?: string;
  startDate?: string;
  endDate?: string;
  page: number;
  size: number;
  sort?: string;
}

export class VoicemailService {
  /**
   * Fetch unread voicemails
   */
  async fetchUnreadVoicemails(
    userId: string,
    filters: VoicemailFilters,
    token?: string
  ): Promise<VoicemailResponse> {
    const params = new URLSearchParams();
    
    if (filters.patientName) {
      params.append('patientName', filters.patientName);
    }
    if (filters.clinicName) {
      params.append('clinicName', filters.clinicName);
    }
    if (filters.clinicNumber) {
      params.append('clinicNumber', filters.clinicNumber);
    }
    if (filters.patientNumber) {
      params.append('patientNumber', filters.patientNumber);
    }
    if (filters.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters.sort) {
      params.append('sortDirection', filters.sort);
    }
    params.append('page', filters.page.toString());
    params.append('size', filters.size.toString());

    // Add the key parameter as required
    params.append('key', 'PEP');

    const queryString = params.toString();
    const url = `/twilio/unreadVoicemails/${userId}${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<VoicemailResponse>(url, token);
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch voicemails');
    }
    
    return response.data;
  }

  /**
   * Get presigned URL for recording
   */
  async getRecordingPresignedUrl(
    userId: string,
    s3Key: string,
    token?: string
  ): Promise<{ url: string }> {
    const url = `/api/communication/${userId}/call/recording/presigned-url?key=${encodeURIComponent(s3Key)}`;
    
    const response = await apiClient.get<{ url: string }>(url, token);
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to get recording URL');
    }
    
    return response.data;
  }

  /**
   * Mark voicemail as read
   */
  async markVoicemailAsRead(
    incomingCallLogId: number,
    userId: string,
    token?: string
  ): Promise<void> {
    const url = '/twilio/updateVoicemailReadBy';

    const params = new URLSearchParams();
    if (incomingCallLogId) {
      params.append('incomingCallLogId', incomingCallLogId.toString());
    }
    if (userId) {
      params.append('userId', userId);
    }
    const queryString = params.toString();
    const response = await apiClient.put(url + (queryString ? `?${queryString}` : ''), {}, token);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to mark voicemail as read');
    }
  }
}

export const voicemailService = new VoicemailService();
